import type { ISuccessApiResponse } from '@wyz/types';
import {
  OverridableQuery,
  UseMutationOptions,
  UseQueryOptions,
  restrictApi,
  useMutation,
  useQuery,
} from '@wyz/utils';
import { useApi, useConfig } from '~/common/context';
import { PurchaseQueryKeys } from '~/purchase/api';
import type {
  IDistributionPricingPolicyDetail,
  IQuoteErrorResponse,
  ISaveDistributionPricingPolicyBrandVariables,
} from '~/purchase/types';

type Options = {
  get?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IDistributionPricingPolicyDetail>,
      IQuoteErrorResponse
    >
  > & {
    args: {
      customerGroupId: string;
    };
  };
  saveBrand?: UseMutationOptions<
    ISuccessApiResponse<IDistributionPricingPolicyDetail>,
    IQuoteErrorResponse,
    ISaveDistributionPricingPolicyBrandVariables
  >;
};

export const useManageDistributionPricingPolicy = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { tenantCode } = useConfig();
  const { callCoreApi, pricingPoliciesDomainBaseUrl } = useApi();

  const getQuery = useQuery({
    queryKey: PurchaseQueryKeys.distributionPricingPolicies.detail(
      options.get?.args.customerGroupId as string,
    ),
    queryFn: () =>
      callCoreApi<ISuccessApiResponse<IDistributionPricingPolicyDetail>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/distribution-pricing-policies/${options.get?.args.customerGroupId}`,
      ),
    refetchOnWindowFocus: false,
    ...options.get,
    enabled: Boolean(options.get?.args.customerGroupId) && options.get?.enabled,
  });

  const saveBrandMutation = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<IDistributionPricingPolicyDetail>>(
        `${pricingPoliciesDomainBaseUrl}/tenants/${tenantCode}/distribution-pricing-policies/${variables.customerGroupId}/brands`,
        { method: 'PUT', body: JSON.stringify(variables.body) },
      ),
    // The save also flips the listing's Oui/Non flag and its "Modifié le" date.
    keysToInvalidateOnSuccess: [
      PurchaseQueryKeys.distributionPricingPolicies.base(),
    ],
    retry: false,
    ...options.saveBrand,
  });

  return restrictApi(options, { get: getQuery, saveBrand: saveBrandMutation });
};
